import { useState } from "react";
import axios from "axios";
import Footer from "../components/Footer";
import { apiUrl } from "../../../utils/config";
import { Ticket, ChevronDown, ChevronUp, MessageSquare, X, Send } from "lucide-react";

const STATUS_COLORS = {
  open:        "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  resolved:    "bg-green-100 text-green-700",
  closed:      "bg-gray-100 text-gray-500",
};
const PRIORITY_COLORS = {
  low:    "bg-slate-100 text-slate-600",
  medium: "bg-blue-100 text-blue-600",
  high:   "bg-orange-100 text-orange-600",
  urgent: "bg-red-100 text-red-700",
};
const Badge = ({ text, map }) => (
  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${(map || {})[text] || "bg-gray-100 text-gray-500"}`}>
    {text?.replace("_", " ")}
  </span>
);
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "";

const INITIAL_FORM = { customer_name:"", customer_email:"", subject:"", category:"general", priority:"medium", description:"" };

// ── Reply Modal ───────────────────────────────────────────────
function ReplyModal({ ticket, onClose, onSent }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState("");

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    setError("");
    try {
      await axios.post(`${apiUrl}/tickets/${ticket.id}/messages`, {
        sender_type: "customer",
        sender_name: ticket.customer_name,
        message: message.trim(),
      });
      onSent();
    } catch (_) {
      setError("Failed to send. Please try again.");
      setSending(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-100">
            <div>
              <p className="text-xs font-mono text-gray-400 mb-0.5">{ticket.ticket_number}</p>
              <h2 className="text-base font-extrabold text-gray-900">{ticket.subject}</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Thread preview */}
          <div className="max-h-56 overflow-y-auto p-5 space-y-3 bg-gray-50">
            {(ticket.messages || []).map((msg, i) => {
              const isAdmin = msg.sender_type === "admin";
              return (
                <div key={msg.id || i} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                    isAdmin
                      ? "bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm"
                      : "bg-orange-500 text-white rounded-tr-sm"
                  }`}>
                    <p className={`text-[11px] font-semibold mb-0.5 ${isAdmin ? "text-gray-400" : "text-orange-100"}`}>
                      {isAdmin ? "Support Team" : "You"}
                    </p>
                    <p className="leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reply form */}
          <div className="p-6 space-y-4">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Your Reply</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Describe your update or follow-up question…"
              rows={4}
              autoFocus
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={send}
                disabled={sending || !message.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-[#f97316] to-red-500 text-white font-extrabold rounded-xl hover:shadow-lg hover:shadow-orange-200 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {sending ? "Sending…" : <><Send size={14} /> Send Reply</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export const Support = () => {
  const [tab, setTab] = useState("new"); // "new" | "lookup"

  // New ticket form
  const [form,       setForm]       = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState(null);

  // Lookup
  const [lookupEmail,  setLookupEmail]  = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [looking,      setLooking]      = useState(false);

  // Thread expand
  const [expanded,       setExpanded]       = useState(null);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [loadingThread,  setLoadingThread]  = useState(false);

  // Reply modal
  const [replyTarget, setReplyTarget] = useState(null);

  const h = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const inputCls = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300";

  const submitTicket = async () => {
    if (!form.customer_name || !form.customer_email || !form.subject || !form.description) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(`${apiUrl}/tickets`, form);
      if (res.data.status) {
        setResult({ status: "success", ticket: res.data.ticket });
        setForm(INITIAL_FORM);
      } else {
        setResult({ status: "error" });
      }
    } catch (_) {
      setResult({ status: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const lookupTickets = async () => {
    if (!lookupEmail.trim()) return;
    setLooking(true);
    setLookupResult(null);
    try {
      const res = await axios.get(`${apiUrl}/tickets/customer`, { params: { email: lookupEmail.trim() } });
      setLookupResult(res.data.tickets || []);
    } catch (_) {
      setLookupResult("error");
    } finally {
      setLooking(false);
    }
  };

  const openThread = async (t) => {
    if (expanded === t.id) { setExpanded(null); setExpandedTicket(null); return; }
    setExpanded(t.id);
    setExpandedTicket(null);
    setLoadingThread(true);
    try {
      const res = await axios.get(`${apiUrl}/tickets/${t.id}`);
      if (res.data.status) setExpandedTicket(res.data.ticket);
    } catch (_) {}
    setLoadingThread(false);
  };

  const refreshThread = async (id) => {
    const res = await axios.get(`${apiUrl}/tickets/${id}`);
    if (res.data.status) {
      setExpandedTicket(res.data.ticket);
      // Also update list
      setLookupResult(prev =>
        Array.isArray(prev)
          ? prev.map(t => t.id === id ? { ...t, status: res.data.ticket.status } : t)
          : prev
      );
    }
  };

  return (
    <div>
      <section className="bg-gradient-to-br from-[#111] to-gray-800 text-white py-20 px-4 text-center">
        <p className="text-sm font-bold text-[#f97316] uppercase tracking-wider mb-3">Help Center</p>
        <h1 className="font-serif text-6xl mb-4">Support <span className="text-[#f97316]">Tickets</span></h1>
        <p className="text-gray-400 text-lg">Submit a new request or track and update your existing tickets.</p>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 mb-8">
          {[["new","New Ticket"],["lookup","My Tickets"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex-1 py-2.5 text-sm font-bold rounded-xl transition-all ${
                tab === key ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── New Ticket Form ── */}
        {tab === "new" && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 space-y-5">
            <h2 className="font-serif text-2xl text-gray-900">Submit a Support Request</h2>

            {result?.status === "success" && (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl px-5 py-4">
                <p className="font-bold text-base mb-1">Ticket submitted!</p>
                <p className="text-sm">Your ticket number is <span className="font-mono font-bold">{result.ticket?.ticket_number}</span>.</p>
                <p className="text-xs text-green-600 mt-1">
                  Use the{" "}
                  <button onClick={() => setTab("lookup")} className="underline font-semibold">My Tickets</button>{" "}
                  tab with your email to track and reply.
                </p>
              </div>
            )}
            {result?.status === "error" && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-2xl px-5 py-4 text-sm">
                Something went wrong. Please try again.
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Your Name *</label>
                <input name="customer_name" value={form.customer_name} onChange={h} placeholder="Full name" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Email *</label>
                <input name="customer_email" value={form.customer_email} onChange={h} type="email" placeholder="email@example.com" className={inputCls} />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 mb-1 block">Subject *</label>
              <input name="subject" value={form.subject} onChange={h} placeholder="Brief description of your issue" className={inputCls} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Category</label>
                <select name="category" value={form.category} onChange={h} className={inputCls}>
                  <option value="general">General</option>
                  <option value="order">Order Issue</option>
                  <option value="billing">Billing</option>
                  <option value="technical">Technical</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Priority</label>
                <select name="priority" value={form.priority} onChange={h} className={inputCls}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 mb-1 block">Description *</label>
              <textarea name="description" value={form.description} onChange={h} rows={5}
                placeholder="Please describe your issue in detail…"
                className={inputCls + " resize-none"}
              />
            </div>

            <button
              onClick={submitTicket}
              disabled={submitting || !form.customer_name || !form.customer_email || !form.subject || !form.description}
              className="w-full bg-gradient-to-r from-[#f97316] to-red-500 text-white font-black py-4 rounded-2xl hover:shadow-xl hover:shadow-orange-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit Ticket"}
            </button>
          </div>
        )}

        {/* ── My Tickets ── */}
        {tab === "lookup" && (
          <div className="space-y-4">
            {/* Email lookup */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-serif text-xl text-gray-900 mb-4">Track Your Tickets</h2>
              <div className="flex gap-3">
                <input
                  value={lookupEmail} onChange={e => setLookupEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && lookupTickets()}
                  type="email" placeholder="Enter your email address"
                  className={inputCls + " flex-1"}
                />
                <button
                  onClick={lookupTickets} disabled={looking || !lookupEmail.trim()}
                  className="bg-orange-500 text-white font-bold px-6 py-3 rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {looking ? "…" : "Find"}
                </button>
              </div>
            </div>

            {lookupResult === "error" && (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-2xl px-5 py-4 text-sm">
                Failed to load tickets. Please try again.
              </div>
            )}

            {Array.isArray(lookupResult) && lookupResult.length === 0 && (
              <div className="text-center py-10 text-gray-400 bg-white rounded-3xl border border-gray-100 shadow-sm">
                <Ticket size={36} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No tickets found for this email.</p>
              </div>
            )}

            {Array.isArray(lookupResult) && lookupResult.map(t => (
              <div key={t.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Ticket row */}
                <div className="flex items-start gap-4 p-5">
                  <button onClick={() => openThread(t)} className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-gray-400">{t.ticket_number}</span>
                      <Badge text={t.status} map={STATUS_COLORS} />
                      <Badge text={t.priority} map={PRIORITY_COLORS} />
                    </div>
                    <p className="font-semibold text-gray-900">{t.subject}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{fmtDate(t.created_at)}</p>
                  </button>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Reply button — only if not closed */}
                    {t.status !== "closed" && (
                      <button
                        onClick={async () => {
                          // Load full thread then open modal
                          let full = expandedTicket?.id === t.id ? expandedTicket : null;
                          if (!full) {
                            const res = await axios.get(`${apiUrl}/tickets/${t.id}`);
                            if (res.data.status) full = res.data.ticket;
                          }
                          setReplyTarget(full || t);
                        }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white text-xs font-bold rounded-xl hover:bg-orange-600 transition-colors"
                      >
                        <MessageSquare size={13} /> Reply
                      </button>
                    )}
                    <button onClick={() => openThread(t)} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors">
                      {expanded === t.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Thread accordion */}
                {expanded === t.id && (
                  <div className="border-t border-gray-100">
                    {loadingThread && !expandedTicket ? (
                      <p className="p-5 text-sm text-gray-400">Loading…</p>
                    ) : expandedTicket?.id === t.id ? (
                      <div className="p-5 space-y-3 max-h-72 overflow-y-auto bg-gray-50">
                        {(expandedTicket.messages || []).map((msg, i) => {
                          const isAdmin = msg.sender_type === "admin";
                          return (
                            <div key={msg.id || i} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                                isAdmin
                                  ? "bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm"
                                  : "bg-orange-500 text-white rounded-tr-sm"
                              }`}>
                                <p className={`text-[11px] font-semibold mb-0.5 ${isAdmin ? "text-gray-400" : "text-orange-100"}`}>
                                  {isAdmin ? "Support Team" : "You"}
                                </p>
                                <p className="leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                                <p className={`text-[10px] mt-1 ${isAdmin ? "text-gray-400" : "text-orange-200"}`}>{fmtDate(msg.created_at)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    {expandedTicket?.id === t.id && t.status === "closed" && (
                      <p className="p-3 text-center text-xs text-gray-400 bg-white border-t border-gray-100">
                        This ticket is closed.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply Modal */}
      {replyTarget && (
        <ReplyModal
          ticket={replyTarget}
          onClose={() => setReplyTarget(null)}
          onSent={() => {
            refreshThread(replyTarget.id);
            setReplyTarget(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
};
